import { Divider, Stack } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { BankDetailsPicker } from 'shared/ui/formik-fields/bank-details-picker';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { LegalEntityPicker } from 'entities/legal-entities/legal-entity-picker';
import { SignerPicker } from 'shared/ui/formik-fields/signer-picker';
import { UserPickerField } from 'features/user/user-picker/ui';
import { ContractTemplatePickerField } from 'shared/ui/formik-fields//contract-template-picker-field';
import { FormValues } from 'components/Contracts/contract.form';
import { EntityRoles } from 'entities/invoice/types';
import { BankDetailsDialog } from 'components/Companies/BankDetails/bank-details-dialog';
import { FormData as BankDetailsFormData } from 'components/Companies/BankDetails/bank-details.form';
import { SignerDialog } from 'components/Companies/Signers/signer-dialog';
import { FormData as SignerFormData } from 'components/Companies/Signers/signers.form';
import { ExternalPickerContainer } from 'components/Mui/external-picker-container';
import { CONTRACT_TYPE_CLIENT, CONTRACT_TYPE_SUPPLIER, ContractType } from '@sr/dto';
import { SwitchField } from 'shared/ui/formik-fields/switch-field';
import { useEffect, useState } from 'react';
import { useBankDetailsList } from 'hooks/bank-details.hook';
import { useSignersList } from 'hooks/signers.hook';
import { useSnack } from 'shared/ui/snack';

type Props = {
  ourCompanyId?: number;
  otherCompanyId?: number;
  contractType?: ContractType;
};

type ContractPartyTarget = 'our' | 'other';

export const ContractFormFields = ({ ourCompanyId, otherCompanyId, contractType = CONTRACT_TYPE_CLIENT }: Props) => {
  const { values, setFieldValue, setFieldError, errors } = useFormikContext<FormValues>();
  const [bankDetailsDialogTarget, setBankDetailsDialogTarget] = useState<ContractPartyTarget | null>(null);
  const [signerDialogTarget, setSignerDialogTarget] = useState<ContractPartyTarget | null>(null);
  const { showSuccess, showError } = useSnack();
  
  useEffect(() => {
    const resetContractEndDate = async () => {
      await setFieldValue('contractEndDate', null);
      if (errors.contractEndDate) {
        setFieldError('contractEndDate', undefined);
      }
    };

    if (values.unlimited) {
      void resetContractEndDate();
    }
  }, [values.unlimited, setFieldValue, setFieldError, errors.contractEndDate]);
  
  const ourRole = contractType === CONTRACT_TYPE_CLIENT ? 'seller' : 'buyer';
  const clientRole = contractType === CONTRACT_TYPE_CLIENT ? 'buyer' : 'seller';
  const ourLegalEntityId = values[`${ourRole}LegalEntityId`] ? Number(values[`${ourRole}LegalEntityId`]) : undefined;
  const otherLegalEntityId = values[`${clientRole}LegalEntityId`] ? Number(values[`${clientRole}LegalEntityId`]) : undefined;
  // наше юрлицо
  const ourBankDetails = useBankDetailsList(ourLegalEntityId);
  const ourSigners = useSignersList(ourLegalEntityId);
  // юрлицо клиента / поставщика
  const otherBankDetails = useBankDetailsList(otherLegalEntityId);
  const otherSigners = useSignersList(otherLegalEntityId);

  const handleAddBankDetails = async (formData: BankDetailsFormData) => {
    if (!bankDetailsDialogTarget) return;

    const targetRole = bankDetailsDialogTarget === 'our' ? ourRole : clientRole;
    const targetBankDetails = bankDetailsDialogTarget === 'our' ? ourBankDetails : otherBankDetails;

    try {
      const created = await targetBankDetails.addBankDetails(formData);
      await setFieldValue(`${targetRole}BankDetailsId`, created.id);
      setBankDetailsDialogTarget(null);
      showSuccess('Реквизиты успешно добавлены');
    } catch (e) {
      setBankDetailsDialogTarget(null);
      showError('Ошибка добавления реквизитов', e);
    }
  };

  const handleAddSigner = async (formData: SignerFormData) => {
    if (!signerDialogTarget) return;

    const targetRole = signerDialogTarget === 'our' ? ourRole : clientRole;
    const targetSigners = signerDialogTarget === 'our' ? ourSigners : otherSigners;

    try {
      const created = await targetSigners.addSigner(formData);
      await setFieldValue(`${targetRole}SignerId`, created.id);
      setSignerDialogTarget(null);
      showSuccess('Подписант успешно добавлен');
    } catch (e) {
      setSignerDialogTarget(null);
      showError('Ошибка добавления подписанта', e);
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      <Field required type="hidden" name="type" value={contractType} />
      <Field label="Название" fullWidth component={TextField} name="name" />
      <Stack direction="row" spacing={2}>
        <Field required label="Дата начала" component={DatePickerField} name="contractStartDate" />
        <Field disabled={values.unlimited} required={!values.unlimited} label="Дата окончания" component={DatePickerField} name="contractEndDate" />
      </Stack>
      <Field name="unlimited" label="Бессрочный" type="checkbox" component={SwitchField} />
      
      {contractType === CONTRACT_TYPE_CLIENT && <Field required label="Тип договора" component={ContractTemplatePickerField} name="contractTemplateId" />}
      {contractType === CONTRACT_TYPE_CLIENT && <Field required label="Ответственный менеджер" component={UserPickerField} name="managerId" isLinkedToCompany />}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Stack direction="column" spacing={2} minWidth={400}>
          <Field
            required
            label="Наше юрлицо"
            fullWidth
            component={LegalEntityPicker}
            disableInactive={true}
            entityRole={EntityRoles.INVOICE_ISSUER}
            name={`${ourRole}LegalEntityId`}
            companyIdFilter={ourCompanyId}
          />
          <ExternalPickerContainer disabled={!ourLegalEntityId || isNaN(ourLegalEntityId)} onClick={() => setBankDetailsDialogTarget('our')}>
            <Field
              required
              label="Наши банковские реквизиты"
              component={BankDetailsPicker}
              disableBlockedForOutgoing={contractType === CONTRACT_TYPE_CLIENT}
              name={`${ourRole}BankDetailsId`}
              legalEntityId={ourLegalEntityId}
              collection={ourBankDetails.collection}
            />
          </ExternalPickerContainer>
          <ExternalPickerContainer disabled={!ourLegalEntityId || isNaN(ourLegalEntityId)} onClick={() => setSignerDialogTarget('our')}>
            <Field
              required
              label="Наш подписант"
              component={SignerPicker}
              legalEntityId={ourLegalEntityId}
              collection={ourSigners.collection}
              name={`${ourRole}SignerId`}
            />
          </ExternalPickerContainer>
        </Stack>
        <Stack direction="column" spacing={2} minWidth={400} width={'100%'}>
          <Field
            required
            label={`Юрлицо ${contractType === CONTRACT_TYPE_CLIENT ? 'клиента' : 'поставщика'}`}
            component={LegalEntityPicker}
            disableInactive={true}
            name={`${clientRole}LegalEntityId`}
            companyIdFilter={otherCompanyId}
          />
          <ExternalPickerContainer disabled={!otherLegalEntityId || isNaN(otherLegalEntityId)} onClick={() => setBankDetailsDialogTarget('other')}>
            <Field
              required
              label={`Банковские реквизиты ${contractType === CONTRACT_TYPE_CLIENT ? 'клиента' : 'поставщика'}`}
              component={BankDetailsPicker}
              name={`${clientRole}BankDetailsId`}
              legalEntityId={otherLegalEntityId}
              collection={otherBankDetails.collection}
            />
          </ExternalPickerContainer>
          <ExternalPickerContainer disabled={!otherLegalEntityId || isNaN(otherLegalEntityId)} onClick={() => setSignerDialogTarget('other')}>
            <Field
              required
              label={`Подписант ${contractType === CONTRACT_TYPE_CLIENT ? 'клиента' : 'поставщика'}`}
              component={SignerPicker}
              legalEntityId={otherLegalEntityId}
              name={`${clientRole}SignerId`}
              collection={otherSigners.collection}
            />
          </ExternalPickerContainer>
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="column" sx={{ mt: 2 }}>
        <BankDetailsDialog
          open={bankDetailsDialogTarget !== null}
          title="Добавить банковские реквизиты"
          onClose={() => setBankDetailsDialogTarget(null)}
          submit={handleAddBankDetails}
        />
        <SignerDialog
          onClose={() => setSignerDialogTarget(null)}
          open={signerDialogTarget !== null}
          submit={handleAddSigner}
          title={'Добавить подписанта'}
        />
      </Stack>
      {contractType === CONTRACT_TYPE_SUPPLIER && <Field name="specialConditions" label="Особые условия" multiline rows={4} component={TextField} />}
    </Stack>
  );
};
